// Configurations API - matching admin-web structure
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../baseQuery';

export interface IConfigurationResponse {
  success: boolean;
  response?: any;
  message?: string;
}

export interface IPatchConfigurationArgs {
  organizationId: number;
  propertyId?: number | null;
  key: string;
  value?: any;
  sensitive?: boolean;
  type?: string;
  description?: string;
}

export interface IPostConfigurationArgs {
  configuration: {
    organizationId: number;
    propertyId?: number;
    key: string;
    value: any;
    sensitive?: boolean;
    type?: string;
    description?: string;
  };
}

// RTK Query API slice for configurations (matching admin-web pattern)
export const configurationsApi = createApi({
  reducerPath: 'configurationsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Configurations'],
  endpoints: (builder) => ({
    // List configurations with optional query string
    listConfigurations: builder.query<IConfigurationResponse, string | void>({
      query: (queryString = '') => ({
        url: `/api/v1/configurations${queryString}`,
        method: 'GET',
      }),
      providesTags: ['Configurations'],
    }),

    // Create new configuration
    postConfiguration: builder.mutation<IConfigurationResponse, IPostConfigurationArgs>({
      query: ({ configuration }) => ({
        url: '/api/v1/configurations',
        method: 'POST',
        body: configuration,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: ['Configurations'],
    }),

    // Update/create configuration (upsert)
    patchConfiguration: builder.mutation<IConfigurationResponse, IPatchConfigurationArgs>({
      query: ({
        organizationId,
        propertyId = null,
        key,
        value,
        sensitive,
        type,
        description,
      }) => ({
        url: '/api/v1/configurations/',
        method: 'PATCH',
        body: {
          organizationId,
          propertyId,
          key,
          value,
          sensitive,
          type,
          description,
        },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: ['Configurations'],
    }),

    // Delete configuration
    deleteConfiguration: builder.mutation<IConfigurationResponse, number>({
      query: (configurationId) => ({
        url: `/api/v1/configurations/${configurationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Configurations'],
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  useListConfigurationsQuery,
  useLazyListConfigurationsQuery,
  usePostConfigurationMutation,
  usePatchConfigurationMutation,
  useDeleteConfigurationMutation,
} = configurationsApi;

